import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { HtmlHeadings } from '../../utils/types.js';
import { Icon } from '../Icon/Icon.js';
import { ButtonActionSubtle } from '../ButtonAction/ButtonAction.js';

var _excluded = ["animation", "children", "className", "heading", "headingTag", "isOpen", "ref", "isAutoFocus", "trigger", "type", "withShadow"];
var NotificationTypes = ['alert', 'warning', 'info', 'success'];
var Notification = props => {
  var {
      animation = true,
      children,
      className,
      heading,
      headingTag = 'h4',
      isOpen,
      ref,
      isAutoFocus = true,
      trigger,
      type,
      withShadow
    } = props,
    attributes = _objectWithoutProperties(props, _excluded);
  var [isComponentHidden, setComponentHidden] = React.useState(!isOpen);
  var [isComponentOpen, setComponentOpen] = React.useState(isOpen);
  var headingRef = React.useRef(null);
  var paragraphRef = React.useRef(null);
  var hcid = "".concat(Notification.name.toLocaleLowerCase(), "-").concat("10.6.2");
  var iconSymbol;
  switch (type) {
    case 'alert':
      iconSymbol = 'Alert';
      break;
    case 'warning':
      iconSymbol = 'Warning';
      break;
    case 'info':
      iconSymbol = 'Info';
      break;
    case 'success':
      iconSymbol = 'Success';
      break;
    default:
      throw new Error('Use alert, warning, info or success type');
  }
  var iconSymbolName = iconSymbol && "message".concat(iconSymbol, "16");
  var classNames = "notification".concat(animation ? ' animate' : '').concat(isComponentOpen ? ' show' : '').concat(isComponentHidden ? ' d-none' : '').concat(type ? " ".concat(type) : '').concat(withShadow ? ' with-shadow' : '').concat(className ? " ".concat(className) : '');
  React.useEffect(() => {
    if (isOpen) {
      setComponentHidden(false);
      setTimeout(() => {
        // open component
        setComponentOpen(true);
      }, 20);
    } else {
      setComponentOpen(false);
    }
  }, [isOpen]);
  React.useEffect(() => {
    // move focus when Notification shows
    if (isAutoFocus && isOpen && isComponentOpen) {
      if (heading) {
        var _headingRef$current;
        headingRef === null || headingRef === void 0 ? void 0 : (_headingRef$current = headingRef.current) === null || _headingRef$current === void 0 ? void 0 : _headingRef$current.focus();
      } else if (children && typeof children === 'string') {
        var _paragraphRef$current;
        paragraphRef === null || paragraphRef === void 0 ? void 0 : (_paragraphRef$current = paragraphRef.current) === null || _paragraphRef$current === void 0 ? void 0 : _paragraphRef$current.focus();
      }
    }
  }, [isOpen, heading, children, isAutoFocus, isComponentOpen]);
  var transitionEnd = () => {
    if (isComponentOpen) {
      setComponentHidden(false);
    } else {
      setComponentHidden(true);
    }
  };
  var headingComponent = heading && /*#__PURE__*/React.createElement(headingTag, {
    tabIndex: -1,
    ref: headingRef,
    className: 'h4'
  }, heading);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classNames,
    onAnimationEnd: transitionEnd,
    ref: ref,
    "aria-label": "notification"
  }, attributes, {
    "data-hcid": hcid
  }), iconSymbolName && /*#__PURE__*/React.createElement(Icon, {
    screenreader: iconSymbol,
    symbol: iconSymbolName,
    type: "svginline"
  }), /*#__PURE__*/React.createElement("div", {
    className: "notification-body"
  }, headingComponent, (() => {
    // eslint-disable-line
    if (!children) {
      return;
    }
    if (typeof children === 'string') {
      // eslint-disable-next-line
      return /*#__PURE__*/React.createElement("p", !heading && {
        ref: paragraphRef,
        tabIndex: -1
      }, children);
    }
    return children; // eslint-disable-line
  })()), trigger && /*#__PURE__*/React.createElement(ButtonActionSubtle, {
    id: trigger.id || 'notification-close',
    onClick: trigger.onClick
  }, trigger.icon && /*#__PURE__*/React.createElement(Icon, {
    screenreader: "Close",
    symbol: trigger.icon || 'controlClose24',
    type: "svginline"
  })));
};
Notification.propTypes = {
  animation: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  heading: PropTypes.string,
  headingTag: PropTypes.oneOf(HtmlHeadings),
  isOpen: PropTypes.bool,
  ref: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLDivElement)
  }),
  isAutoFocus: PropTypes.bool,
  trigger: PropTypes.shape({
    icon: PropTypes.string,
    id: PropTypes.string,
    onclick: PropTypes.func
  }),
  type: PropTypes.oneOf(NotificationTypes).isRequired,
  withShadow: PropTypes.bool
}; // eslint-disable-line

export { Notification, NotificationTypes };
